import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { pdf, Document } from "@react-pdf/renderer";
import { Select, Button, Space, message } from "antd";
import { DownloadOutlined, UploadOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import "../../styles/pages/facultyProfilePage.scss";
import WorkloadSection from "../../components/FacultyProfile/WorkloadSection";
import TeachingLoadTabs from "../../components/FacultyProfile/TeachingLoadTabs";
import ConsultationHoursSection from "../../components/FacultyProfile/ConsultationHoursSection";
import TermDetailsSection from "../../components/FacultyProfile/TermDetailsSection";
import FacultyProfileModal from "../../components/FacultyProfile/FacultyProfileModal";
import FsrImportModal from "../../components/FacultyProfile/FsrImportModal";
import FsrBulkImportModal from "../../components/FacultyProfile/FsrBulkImportModal";
import GeneratePortfolioModal from "../../components/FacultyProfile/GeneratePortfolioModal";
import TermSelect from "../../components/TermSelect";
import PortfolioFormat from "../../generatePdf/documents/PortfolioFormat";
import {
  sortItemsByTermDesc,
  sortTermOptionsDesc,
  sortTermsAsc,
  getDuplicateTeachingLoadKeys,
  teachingLoadDuplicateKey,
} from "../../utils/generalUtils";
import { facultyProfileConfig } from "../../config/facultyProfileConfig";
import useAcademicTerms from "../../hooks/useAcademicTerms";

const SECTION_KEYS = [
  "teachingLoad",
  "research",
  "administrativeWork",
  "tutorialWork",
  "extensionService",
  "advising",
  "conferencePresentation",
  "seminarWorkshop",
  "studyLoad",
  "limitedPractice",
  "recipientOrNominee",
  "consultationHours",
];

export default function FacultyProfilePage() {
  const { details } = useSelector((state) => state.accountDetailSlice);

  const userName = details?.first_name
    ? `${details.first_name} ${details.last_name}`
    : details?.email;

  const config = facultyProfileConfig;
  const termOptions = useAcademicTerms(true);

  // -------------------------
  // Filters
  // -------------------------

  const [selectedTerms, setSelectedTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState();

  // -------------------------
  // Section collapse/expand — all sections start expanded
  // -------------------------

  const [collapsedSections, setCollapsedSections] = useState(() =>
    Object.fromEntries(SECTION_KEYS.map((key) => [key, false]))
  );

  const toggleSection = (key) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const collapseAll = () =>
    setCollapsedSections(Object.fromEntries(SECTION_KEYS.map((key) => [key, true])));

  const expandAll = () =>
    setCollapsedSections(Object.fromEntries(SECTION_KEYS.map((key) => [key, false])));

  const allExpanded = SECTION_KEYS.every((key) => !collapsedSections[key]);
  const toggleAllSections = () => (allExpanded ? collapseAll() : expandAll());

  useEffect(() => {
    if (termOptions.length > 0 && !selectedTerm) {
      setSelectedTerm(termOptions[0].value);
    }
  }, [termOptions, selectedTerm]);

  const filterByTerms = (data) => {
    return selectedTerms.length === 0
      ? data
      : data.filter((item) => selectedTerms.includes(item.academic_term));
  };

  // -------------------------
  // Data Fetching
  // -------------------------

  const [teachingLoadUpouData, setTeachingLoadUpouData] = useState([]);
  const [teachingLoadOtherUpData, setTeachingLoadOtherUpData] = useState([]);
  const [teachingLoadOutsideUpData, setTeachingLoadOutsideUpData] = useState(
    [],
  );
  const [researchData, setResearchData] = useState([]);
  const [administrativeWorkData, setAdministrativeWorkData] = useState([]);
  const [tutorialWorkData, setTutorialWorkData] = useState([]);
  const [extensionServiceData, setExtensionServiceData] = useState([]);
  const [studyLoadData, setStudyLoadData] = useState([]);
  const [limitedPracticeData, setLimitedPracticeData] = useState([]);
  const [recipientOrNomineeData, setRecipientOrNomineeData] = useState([]);
  const [consultationHoursData, setConsultationHoursData] = useState([]);
  const [advisingData, setAdvisingData] = useState([]);
  const [conferencePresentationData, setConferencePresentationData] = useState([]);
  const [seminarWorkshopData, setSeminarWorkshopData] = useState([]);

  const endpoints = [
    { endpoint: "research", setter: setResearchData },
    { endpoint: "teaching-load-upou", setter: setTeachingLoadUpouData },
    { endpoint: "teaching-load-other-up", setter: setTeachingLoadOtherUpData },
    {
      endpoint: "teaching-load-outside-up",
      setter: setTeachingLoadOutsideUpData,
    },
    { endpoint: "administrative-work", setter: setAdministrativeWorkData },
    { endpoint: "tutorial-work", setter: setTutorialWorkData },
    { endpoint: "extension-service", setter: setExtensionServiceData },
    { endpoint: "study-load", setter: setStudyLoadData },
    {
      endpoint: "limited-practice-of-profession",
      setter: setLimitedPracticeData,
    },
    { endpoint: "recipient-or-nominee", setter: setRecipientOrNomineeData },
    { endpoint: "consultation-hours", setter: setConsultationHoursData },
    { endpoint: "advising", setter: setAdvisingData },
    { endpoint: "conference-presentations", setter: setConferencePresentationData },
    { endpoint: "seminar-workshops", setter: setSeminarWorkshopData },
  ];

  const fetchData = async (endpoint, setter) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("api_token")}`,
        },
      });
      const data = await res.json();
      setter(data);
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
    }
  };

  const refetchAllSections = () => {
    endpoints.forEach(({ endpoint, setter }) => {
      fetchData(endpoint, setter);
    });
  };

  useEffect(() => {
    refetchAllSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStudyLoad = studyLoadData?.find(
    (item) => item.academic_term === selectedTerm,
  );
  const selectedLimitedPractice = limitedPracticeData?.find(
    (item) => item.academic_term === selectedTerm,
  );
  const selectedRecipientOrNominee = recipientOrNomineeData?.find(
    (item) => item.academic_term === selectedTerm,
  );

  // Unique terms that the faculty has at least one record in (for Generate Portfolio modal)
  const filledTermOptions = useMemo(() => {
    const datasets = [
      teachingLoadUpouData,
      teachingLoadOtherUpData,
      teachingLoadOutsideUpData,
      researchData,
      administrativeWorkData,
      tutorialWorkData,
      extensionServiceData,
      studyLoadData,
      limitedPracticeData,
      recipientOrNomineeData,
      advisingData,
    ];
    const seen = new Set();
    datasets.forEach((dataset) => {
      dataset.forEach((item) => {
        if (item.academic_term) seen.add(item.academic_term);
      });
    });
    return sortTermOptionsDesc(
      Array.from(seen).map((t) => ({ label: t, value: t })),
    );
  }, [
    teachingLoadUpouData,
    teachingLoadOtherUpData,
    teachingLoadOutsideUpData,
    researchData,
    administrativeWorkData,
    tutorialWorkData,
    extensionServiceData,
    studyLoadData,
    limitedPracticeData,
    recipientOrNomineeData,
    advisingData,
  ]);

  // -------------------------
  // Teaching Load Tabs
  // -------------------------

  const [activeTeachingTab, setActiveTeachingTab] = useState("upou");

  const teachingLoadMap = {
    upou: {
      endpoint: "teaching-load-upou",
      data: teachingLoadUpouData,
      setter: setTeachingLoadUpouData,
    },
    otherUp: {
      endpoint: "teaching-load-other-up",
      data: teachingLoadOtherUpData,
      setter: setTeachingLoadOtherUpData,
    },
    outsideUp: {
      endpoint: "teaching-load-outside-up",
      data: teachingLoadOutsideUpData,
      setter: setTeachingLoadOutsideUpData,
    },
  };

  const getTeachingLoadData = () => {
    const { data } = teachingLoadMap[activeTeachingTab];
    return filterByTerms(sortItemsByTermDesc(data));
  };

  const teachingLoadImportGuides = {
    upou: {
      sampleFileUrl: "/samples/fsr/teaching-load-upou-sample.csv",
      sampleFileName: "teaching-load-upou-sample.csv",
      columnsGuide: [
        { name: "Academic Term" },
        { name: "Course No." },
        { name: "Course Title" },
        { name: "Course Type", note: "Regular, Thesis, or Capstone" },
        { name: "No. of Students" },
        { name: "Course Credits" },
        { name: "Team Teaching", note: "Yes or No" },
        { name: "Percentage", note: "required if Team Teaching is Yes" },
        { name: "Proof URL", note: "optional" },
      ],
    },
    otherUp: {
      sampleFileUrl: "/samples/fsr/teaching-load-other-up-sample.csv",
      sampleFileName: "teaching-load-other-up-sample.csv",
      columnsGuide: [
        { name: "Academic Term" },
        { name: "UP Unit" },
        { name: "Course No." },
        { name: "Section Code" },
        { name: "Room" },
        { name: "Days" },
        { name: "Time" },
        { name: "Hrs. per week" },
        { name: "No. of Students" },
        { name: "Course Credits" },
        { name: "Team Teaching", note: "Yes or No" },
        { name: "Percentage", note: "required if Team Teaching is Yes" },
        { name: "Proof URL", note: "optional" },
      ],
    },
    outsideUp: {
      sampleFileUrl: "/samples/fsr/teaching-load-outside-up-sample.csv",
      sampleFileName: "teaching-load-outside-up-sample.csv",
      columnsGuide: [
        { name: "Academic Term" },
        { name: "College" },
        { name: "No. of Subjects" },
        { name: "No. of Units" },
        { name: "Proof URL", note: "optional" },
      ],
    },
  };

  // Same Academic Term + Course No. entered twice (In UPOU or In Other UP
  // Units) — flag it in the UI and block portfolio generation until fixed.
  const teachingLoadDuplicates = useMemo(() => {
    const upouKeys = getDuplicateTeachingLoadKeys(teachingLoadUpouData);
    const otherUpKeys = getDuplicateTeachingLoadKeys(teachingLoadOtherUpData);

    const describe = (data, keys, label) =>
      data
        .filter((item) => keys.has(teachingLoadDuplicateKey(item)))
        .map((item) => `${label}: ${item.academic_term} — ${item.course_number}`);

    const messages = [
      ...describe(teachingLoadUpouData, upouKeys, "In UPOU"),
      ...describe(teachingLoadOtherUpData, otherUpKeys, "In Other UP Units"),
    ];

    return {
      hasDuplicates: upouKeys.size > 0 || otherUpKeys.size > 0,
      tooltip: Array.from(new Set(messages)).join("; "),
    };
  }, [teachingLoadUpouData, teachingLoadOtherUpData]);

  // -------------------------
  // CRUD Modal State
  // -------------------------

  const [modalState, setModalState] = useState({ open: false });

  const openModal = ({
    mode,
    showDelete,
    title,
    config,
    initialEditValues,
    onSubmit,
    onDelete,
  }) => {
    setModalState({
      open: true,
      mode,
      showDelete,
      title,
      config,
      initialEditValues,
      onSubmit,
      onDelete,
    });
  };

  const closeModal = () => {
    setModalState({ open: false });
  };

  // --------------------------------
  // Import Modal State
  // --------------------------------

  const [importModalState, setImportModalState] = useState({ open: false });

  const openImportModal = ({ title, endpoint, setter, sampleFileUrl, sampleFileName, columnsGuide }) => {
    setImportModalState({
      open: true,
      title,
      endpoint,
      setter,
      sampleFileUrl,
      sampleFileName,
      columnsGuide,
    });
  };

  const closeImportModal = () => {
    setImportModalState({ open: false });
  };

  // --------------------------------
  // Bulk ("Import All Sections") Modal State
  // --------------------------------

  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // --------------------------------
  // Portfolio Modal State
  // --------------------------------

  const [portfolioModalState, setPortfolioModalState] = useState({
    open: false,
  });

  const openPortfolioModal = ({ onSubmit }) => {
    setPortfolioModalState({
      open: true,
      onSubmit,
    });
  };

  const closePortfolioModal = () => {
    setPortfolioModalState({ open: false });
  };

  // -------------------------
  // Sorting
  // -------------------------

  // Sort selected terms label
  const handleTermChange = (values) => {
    const sortedValues = sortTermsAsc(values);
    setSelectedTerms(sortedValues);
  };

  // Sort consultation hours
  const dayOrder = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  const sortByDay = (data) => {
    return [...data].sort((a, b) => {
      return dayOrder[a.day] - dayOrder[b.day];
    });
  };

  // -------------------------
  // CRUD Handlers
  // -------------------------

  const handleAdd = async (endpoint, values, setter, currentData) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("api_token")}`,
      },
      body: JSON.stringify(values),
    });

    const newItem = await res.json();

    if (!res.ok) {
      throw new Error(newItem?.message || "Failed to add entry.");
    }

    setter([...currentData, newItem]);
  };

  const handleEdit = async (endpoint, id, values, setter, currentData) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/${endpoint}/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("api_token")}`,
        },
        body: JSON.stringify(values),
      },
    );

    const updatedItem = await res.json();

    if (!res.ok) {
      throw new Error(updatedItem?.message || "Failed to save changes.");
    }

    setter(currentData.map((item) => (item.id === id ? updatedItem : item)));
  };

  const handleDelete = async (endpoint, id, setter, currentData) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("api_token")}`,
        },
      });

      setter(currentData.filter((item) => item.id !== id));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // ----------------------------
  // Generate Portfolio Handler
  // ----------------------------

  const handleGeneratePortfolio = async ({ academic_terms, sections }) => {
    const selectedTerms = academic_terms || [];

    const filterTerms = (items) => {
      if (!selectedTerms.length) return items;

      return items.filter((item) => selectedTerms.includes(item.academic_term));
    };

    const sectionMap = {
      teaching_load_upou: {
        title: "Teaching Load In UPOU",
        items: filterTerms(teachingLoadUpouData),
        fields: config.teachingLoad.tabs.upou.fields,
      },

      teaching_load_other_up: {
        title: "Teaching Load In Other UP Units",
        items: filterTerms(teachingLoadOtherUpData),
        fields: config.teachingLoad.tabs.otherUp.fields,
      },

      teaching_load_outside_up: {
        title: "Teaching Load Outside UP",
        items: filterTerms(teachingLoadOutsideUpData),
        fields: config.teachingLoad.tabs.outsideUp.fields,
      },

      research: {
        title: config.research.title,
        items: filterTerms(researchData),
        fields: config.research.fields,
      },

      administrative_work: {
        title: config.administrativeWork.title,
        items: filterTerms(administrativeWorkData),
        fields: config.administrativeWork.fields,
      },

      tutorial_work: {
        title: config.tutorialWork.title,
        items: filterTerms(tutorialWorkData),
        fields: config.tutorialWork.fields,
      },

      extension_service: {
        title: config.extensionService.title,
        items: filterTerms(extensionServiceData),
        fields: config.extensionService.fields,
      },

      advising: {
        title: config.advising.title,
        items: advisingData,
        fields: config.advising.fields,
      },

      conference_presentations: {
        title: config.conferencePresentation.title,
        items: conferencePresentationData,
        fields: config.conferencePresentation.fields,
      },

      seminar_workshops: {
        title: config.seminarWorkshop.title,
        items: seminarWorkshopData,
        fields: config.seminarWorkshop.fields,
      },
    };

    const selectedSections = sections.map((key) => sectionMap[key]);

    const blob = await pdf(
      <Document>
        <PortfolioFormat
          contents={{
            facultyName: userName,
            selectedTerms,
            sections: selectedSections,
          }}
        />
        ,
      </Document>,
    ).toBlob();

    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  return (
    <div className="faculty-profile-page">
      {/* ========================= */}
      {/* WORKLOADS */}
      {/* ========================= */}

      <div className="section-title">Workloads</div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by academic term"
            value={selectedTerms}
            onChange={handleTermChange}
            options={termOptions}
            maxTagCount={2}
            style={{ width: "670px" }}
          />
        </div>

        <Space>
          <Button
            icon={allExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={toggleAllSections}
          >
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>

          <Button
            icon={<UploadOutlined />}
            onClick={() => setBulkImportOpen(true)}
          >
            Import All Sections
          </Button>

          <Button
            style={{ background: "#00573F", width: "fit-content" }}
            icon={<DownloadOutlined />}
            type="primary"
            onClick={() => {
              if (teachingLoadDuplicates.hasDuplicates) {
                message.error(
                  "Please fix your duplicate Teaching Load entries (same Academic Term and Course No.) before generating your portfolio.",
                );
                return;
              }

              openPortfolioModal({
                onSubmit: handleGeneratePortfolio,
              });
            }}
          >
            Generate Portfolio
          </Button>
        </Space>
      </div>

      {/* ========================= */}
      {/* Teaching Load */}
      {/* ========================= */}

      <WorkloadSection
        title={config.teachingLoad.title}
        collapsed={collapsedSections.teachingLoad}
        onToggleCollapse={() => toggleSection("teachingLoad")}
        selectedTerms={selectedTerms}
        warningTooltip={
          teachingLoadDuplicates.hasDuplicates
            ? teachingLoadDuplicates.tooltip
            : null
        }
        portfolioProps={{
          title: `${config.teachingLoad.title} — ${config.teachingLoad.tabs[activeTeachingTab].label}`,
          items: getTeachingLoadData(),
          fields: config.teachingLoad.tabs[activeTeachingTab].fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() => {
          const tab = config.teachingLoad.tabs[activeTeachingTab];
          const { endpoint, data, setter } = teachingLoadMap[activeTeachingTab];

          openModal({
            mode: "add",
            showDelete: false,
            title: `${config.teachingLoad.title} — ${tab.label}`,
            config: tab,
            onSubmit: (values) => handleAdd(endpoint, values, setter, data),
          });
        }}
        onImport={() => {
          const tab = config.teachingLoad.tabs[activeTeachingTab];
          const { endpoint, setter } = teachingLoadMap[activeTeachingTab];
          const guide = teachingLoadImportGuides[activeTeachingTab];

          openImportModal({
            title: `Import ${config.teachingLoad.title} — ${tab.label}`,
            endpoint,
            setter,
            sampleFileUrl: guide.sampleFileUrl,
            sampleFileName: guide.sampleFileName,
            columnsGuide: guide.columnsGuide,
          });
        }}
      >
        <TeachingLoadTabs
          activeTab={activeTeachingTab}
          onTabChange={setActiveTeachingTab}
          selectedTerms={selectedTerms}
          onEdit={(rowData) => {
            const tab = config.teachingLoad.tabs[activeTeachingTab];
            const { endpoint, data, setter } =
              teachingLoadMap[activeTeachingTab];

            openModal({
              mode: "edit",
              showDelete: true,
              title: `${config.teachingLoad.title} — ${tab.label}`,
              config: tab,
              initialEditValues: rowData,
              onSubmit: (values) =>
                handleEdit(endpoint, rowData.id, values, setter, data),
              onDelete: () => handleDelete(endpoint, rowData.id, setter, data),
            });
          }}
          teachingLoadUpouData={sortItemsByTermDesc(teachingLoadUpouData)}
          teachingLoadOtherUpData={sortItemsByTermDesc(teachingLoadOtherUpData)}
          teachingLoadOutsideUpData={sortItemsByTermDesc(
            teachingLoadOutsideUpData,
          )}
        />
      </WorkloadSection>

      {/* ========================= */}
      {/* Research */}
      {/* ========================= */}

      <WorkloadSection
        title={config.research.title}
        collapsed={collapsedSections.research}
        onToggleCollapse={() => toggleSection("research")}
        columns={config.research.columns}
        data={sortItemsByTermDesc(researchData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.research.title,
          items: filterByTerms(sortItemsByTermDesc(researchData)),
          fields: config.research.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: "Research, Textbook Writing, Course Dev., & Creative Work",
            config: config.research,
            onSubmit: (values) =>
              handleAdd("research", values, setResearchData, researchData),
          })
        }
        onImport={() =>
          openImportModal({
            title: "Import Research, Textbook Writing, Course Dev., & Creative Work",
            endpoint: "research",
            setter: setResearchData,
            sampleFileUrl: "/samples/fsr/research-sample.csv",
            sampleFileName: "research-sample.csv",
            columnsGuide: [
              { name: "Academic Term" },
              { name: "Title" },
              { name: "Nature of Involvement", note: "optional" },
              { name: "Start Date", note: "optional" },
              { name: "End Date", note: "optional" },
              { name: "Funded?", note: "Yes or No" },
              { name: "Approved Credit Units" },
              { name: "Proof URL", note: "optional" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: "Research, Textbook Writing, Course Dev., & Creative Work",
            config: config.research,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "research",
                rowData.id,
                values,
                setResearchData,
                researchData,
              ),
            onDelete: () =>
              handleDelete(
                "research",
                rowData.id,
                setResearchData,
                researchData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* Administrative Work */}
      {/* ========================= */}

      <WorkloadSection
        title={config.administrativeWork.title}
        collapsed={collapsedSections.administrativeWork}
        onToggleCollapse={() => toggleSection("administrativeWork")}
        columns={config.administrativeWork.columns}
        data={sortItemsByTermDesc(administrativeWorkData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.administrativeWork.title,
          items: filterByTerms(sortItemsByTermDesc(administrativeWorkData)),
          fields: config.administrativeWork.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.administrativeWork.title,
            config: config.administrativeWork,
            onSubmit: (values) =>
              handleAdd(
                "administrative-work",
                values,
                setAdministrativeWorkData,
                administrativeWorkData,
              ),
          })
        }
        onImport={() =>
          openImportModal({
            title: `Import ${config.administrativeWork.title}`,
            endpoint: "administrative-work",
            setter: setAdministrativeWorkData,
            sampleFileUrl: "/samples/fsr/administrative-work-sample.csv",
            sampleFileName: "administrative-work-sample.csv",
            columnsGuide: [
              { name: "Academic Term" },
              { name: "Position/Nature" },
              { name: "Office/Unit" },
              { name: "Approved Credit Units" },
              { name: "Proof URL", note: "optional" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.administrativeWork.title,
            config: config.administrativeWork,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "administrative-work",
                rowData.id,
                values,
                setAdministrativeWorkData,
                administrativeWorkData,
              ),
            onDelete: () =>
              handleDelete(
                "administrative-work",
                rowData.id,
                setAdministrativeWorkData,
                administrativeWorkData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* Tutorial Work */}
      {/* ========================= */}

      <WorkloadSection
        title={config.tutorialWork.title}
        collapsed={collapsedSections.tutorialWork}
        onToggleCollapse={() => toggleSection("tutorialWork")}
        columns={config.tutorialWork.columns}
        data={sortItemsByTermDesc(tutorialWorkData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.tutorialWork.title,
          items: filterByTerms(sortItemsByTermDesc(tutorialWorkData)),
          fields: config.tutorialWork.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.tutorialWork.title,
            config: config.tutorialWork,
            onSubmit: (values) =>
              handleAdd(
                "tutorial-work",
                values,
                setTutorialWorkData,
                tutorialWorkData,
              ),
          })
        }
        onImport={() =>
          openImportModal({
            title: `Import ${config.tutorialWork.title}`,
            endpoint: "tutorial-work",
            setter: setTutorialWorkData,
            sampleFileUrl: "/samples/fsr/tutorial-work-sample.csv",
            sampleFileName: "tutorial-work-sample.csv",
            columnsGuide: [
              { name: "Academic Term" },
              { name: "Course" },
              { name: "Learning Center", note: "optional" },
              { name: "No. of Students" },
              { name: "Proof URL", note: "optional" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.tutorialWork.title,
            config: config.tutorialWork,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "tutorial-work",
                rowData.id,
                values,
                setTutorialWorkData,
                tutorialWorkData,
              ),
            onDelete: () =>
              handleDelete(
                "tutorial-work",
                rowData.id,
                setTutorialWorkData,
                tutorialWorkData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* Extension */}
      {/* ========================= */}

      <WorkloadSection
        title={config.extensionService.title}
        collapsed={collapsedSections.extensionService}
        onToggleCollapse={() => toggleSection("extensionService")}
        columns={config.extensionService.columns}
        data={sortItemsByTermDesc(extensionServiceData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.extensionService.title,
          items: filterByTerms(sortItemsByTermDesc(extensionServiceData)),
          fields: config.extensionService.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.extensionService.title,
            config: config.extensionService,
            onSubmit: (values) =>
              handleAdd(
                "extension-service",
                values,
                setExtensionServiceData,
                extensionServiceData,
              ),
          })
        }
        onImport={() =>
          openImportModal({
            title: `Import ${config.extensionService.title}`,
            endpoint: "extension-service",
            setter: setExtensionServiceData,
            sampleFileUrl: "/samples/fsr/extension-service-sample.csv",
            sampleFileName: "extension-service-sample.csv",
            columnsGuide: [
              { name: "Academic Term" },
              { name: "Position/Nature" },
              { name: "Office/Unit" },
              { name: "Approved Credit Units" },
              { name: "Proof URL", note: "optional" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.extensionService.title,
            config: config.extensionService,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "extension-service",
                rowData.id,
                values,
                setExtensionServiceData,
                extensionServiceData,
              ),
            onDelete: () =>
              handleDelete(
                "extension-service",
                rowData.id,
                setExtensionServiceData,
                extensionServiceData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* ADVISING */}
      {/* ========================= */}

      <WorkloadSection
        title={config.advising.title}
        collapsed={collapsedSections.advising}
        onToggleCollapse={() => toggleSection("advising")}
        columns={config.advising.columns}
        data={advisingData}
        selectedTerms={[]}
        includedInFsr={false}
        portfolioProps={{
          title: config.advising.title,
          items: advisingData,
          fields: config.advising.fields,
          selectedTerms: [],
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.advising.title,
            config: config.advising,
            onSubmit: (values) =>
              handleAdd(
                "advising",
                values,
                setAdvisingData,
                advisingData,
              ),
          })
        }
        onImport={() =>
          openImportModal({
            title: `Import ${config.advising.title}`,
            endpoint: "advising",
            setter: setAdvisingData,
            sampleFileUrl: "/samples/fsr/advising-sample.csv",
            sampleFileName: "advising-sample.csv",
            columnsGuide: [
              { name: "Name of Student" },
              { name: "Degree Program" },
              { name: "Academic Term Graduated" },
              { name: "Proof URL", note: "optional" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.advising.title,
            config: config.advising,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "advising",
                rowData.id,
                values,
                setAdvisingData,
                advisingData,
              ),
            onDelete: () =>
              handleDelete(
                "advising",
                rowData.id,
                setAdvisingData,
                advisingData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* CONFERENCES/SEMINARS/WORKSHOPS */}
      {/* ========================= */}

      <div className="section-title">Conferences/Seminars/Workshops</div>

      <WorkloadSection
        title={config.conferencePresentation.title}
        collapsed={collapsedSections.conferencePresentation}
        onToggleCollapse={() => toggleSection("conferencePresentation")}
        columns={config.conferencePresentation.columns}
        data={conferencePresentationData}
        selectedTerms={[]}
        includedInFsr={false}
        portfolioProps={{
          title: config.conferencePresentation.title,
          items: conferencePresentationData,
          fields: config.conferencePresentation.fields,
          selectedTerms: [],
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.conferencePresentation.title,
            config: config.conferencePresentation,
            onSubmit: (values) =>
              handleAdd(
                "conference-presentations",
                values,
                setConferencePresentationData,
                conferencePresentationData,
              ),
          })
        }
        onImport={() =>
          openImportModal({
            title: `Import ${config.conferencePresentation.title}`,
            endpoint: "conference-presentations",
            setter: setConferencePresentationData,
            sampleFileUrl: "/samples/fsr/conference-presentations-sample.csv",
            sampleFileName: "conference-presentations-sample.csv",
            columnsGuide: [
              { name: "Title" },
              { name: "Conference Name" },
              { name: "Date", note: "optional" },
              { name: "Proof URL", note: "optional" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.conferencePresentation.title,
            config: config.conferencePresentation,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "conference-presentations",
                rowData.id,
                values,
                setConferencePresentationData,
                conferencePresentationData,
              ),
            onDelete: () =>
              handleDelete(
                "conference-presentations",
                rowData.id,
                setConferencePresentationData,
                conferencePresentationData,
              ),
          })
        }
      />

      <WorkloadSection
        title={config.seminarWorkshop.title}
        collapsed={collapsedSections.seminarWorkshop}
        onToggleCollapse={() => toggleSection("seminarWorkshop")}
        columns={config.seminarWorkshop.columns}
        data={seminarWorkshopData}
        selectedTerms={[]}
        includedInFsr={false}
        portfolioProps={{
          title: config.seminarWorkshop.title,
          items: seminarWorkshopData,
          fields: config.seminarWorkshop.fields,
          selectedTerms: [],
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.seminarWorkshop.title,
            config: config.seminarWorkshop,
            onSubmit: (values) =>
              handleAdd(
                "seminar-workshops",
                values,
                setSeminarWorkshopData,
                seminarWorkshopData,
              ),
          })
        }
        onImport={() =>
          openImportModal({
            title: `Import ${config.seminarWorkshop.title}`,
            endpoint: "seminar-workshops",
            setter: setSeminarWorkshopData,
            sampleFileUrl: "/samples/fsr/seminar-workshops-sample.csv",
            sampleFileName: "seminar-workshops-sample.csv",
            columnsGuide: [
              { name: "Seminar/Workshop Name" },
              { name: "Role" },
              { name: "Date", note: "optional" },
              { name: "Proof URL", note: "optional" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.seminarWorkshop.title,
            config: config.seminarWorkshop,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "seminar-workshops",
                rowData.id,
                values,
                setSeminarWorkshopData,
                seminarWorkshopData,
              ),
            onDelete: () =>
              handleDelete(
                "seminar-workshops",
                rowData.id,
                setSeminarWorkshopData,
                seminarWorkshopData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* OTHER INFORMATION */}
      {/* ========================= */}

      <div className="section-title">Other Information</div>

      <TermSelect
        value={selectedTerm}
        onChange={setSelectedTerm}
        width="310px"
        extended
      />

      {/* Term Detail Sections */}

      <TermDetailsSection
        title={config.studyLoad.title}
        collapsed={collapsedSections.studyLoad}
        onToggleCollapse={() => toggleSection("studyLoad")}
        fields={config.studyLoad.fields}
        data={selectedStudyLoad}
        selectedTerm={selectedTerm}
        onEdit={() => {
          openModal({
            mode: "edit",
            showDelete: false,
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.studyLoad.title}
                <span>{selectedTerm}</span>
              </div>
            ),
            config: config.studyLoad,
            initialEditValues: selectedStudyLoad,
            onSubmit: (values) => {
              if (selectedStudyLoad) {
                handleEdit(
                  "study-load",
                  selectedStudyLoad.id,
                  values,
                  setStudyLoadData,
                  studyLoadData,
                );
              } else {
                handleAdd(
                  "study-load",
                  {
                    ...values,
                    academic_term: selectedTerm,
                  },
                  setStudyLoadData,
                  studyLoadData,
                );
              }
            },
          });
        }}
      />

      <TermDetailsSection
        title={config.limitedPractice.title}
        collapsed={collapsedSections.limitedPractice}
        onToggleCollapse={() => toggleSection("limitedPractice")}
        fields={config.limitedPractice.fields}
        data={selectedLimitedPractice}
        selectedTerm={selectedTerm}
        onEdit={() => {
          openModal({
            mode: "edit",
            showDelete: false,
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.limitedPractice.title}
                <span>{selectedTerm}</span>
              </div>
            ),
            config: config.limitedPractice,
            initialEditValues: selectedLimitedPractice,
            onSubmit: (values) => {
              if (selectedLimitedPractice) {
                handleEdit(
                  "limited-practice-of-profession",
                  selectedLimitedPractice.id,
                  values,
                  setLimitedPracticeData,
                  limitedPracticeData,
                );
              } else {
                handleAdd(
                  "limited-practice-of-profession",
                  {
                    ...values,
                    academic_term: selectedTerm,
                  },
                  setLimitedPracticeData,
                  limitedPracticeData,
                );
              }
            },
          });
        }}
      />

      <TermDetailsSection
        title={config.recipientOrNominee.title}
        collapsed={collapsedSections.recipientOrNominee}
        onToggleCollapse={() => toggleSection("recipientOrNominee")}
        fields={config.recipientOrNominee.fields}
        data={selectedRecipientOrNominee}
        selectedTerm={selectedTerm}
        onEdit={() => {
          openModal({
            mode: "edit",
            showDelete: false,
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.recipientOrNominee.title}
                <span>{selectedTerm}</span>
              </div>
            ),
            config: config.recipientOrNominee,
            initialEditValues: selectedRecipientOrNominee,
            onSubmit: (values) => {
              if (selectedRecipientOrNominee) {
                handleEdit(
                  "recipient-or-nominee",
                  selectedRecipientOrNominee.id,
                  values,
                  setRecipientOrNomineeData,
                  recipientOrNomineeData,
                );
              } else {
                handleAdd(
                  "recipient-or-nominee",
                  {
                    ...values,
                    academic_term: selectedTerm,
                  },
                  setRecipientOrNomineeData,
                  recipientOrNomineeData,
                );
              }
            },
          });
        }}
      />

      {/* ========================= */}
      {/* Consultation Hours */}
      {/* ========================= */}

      <ConsultationHoursSection
        columns={config.consultationHours.columns}
        data={sortByDay(consultationHoursData)}
        collapsed={collapsedSections.consultationHours}
        onToggleCollapse={() => toggleSection("consultationHours")}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            config: config.consultationHours,
            title: config.consultationHours.title,
            onSubmit: (values) =>
              handleAdd(
                "consultation-hours",
                values,
                setConsultationHoursData,
                consultationHoursData,
              ),
          })
        }
        onImport={() =>
          openImportModal({
            title: `Import ${config.consultationHours.title}`,
            endpoint: "consultation-hours",
            setter: setConsultationHoursData,
            sampleFileUrl: "/samples/fsr/consultation-hours-sample.csv",
            sampleFileName: "consultation-hours-sample.csv",
            columnsGuide: [
              { name: "Day" },
              { name: "Start Time" },
              { name: "End Time" },
              { name: "Place" },
            ],
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.consultationHours.title,
            config: config.consultationHours,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "consultation-hours",
                rowData.id,
                values,
                setConsultationHoursData,
                consultationHoursData,
              ),
            onDelete: () =>
              handleDelete(
                "consultation-hours",
                rowData.id,
                setConsultationHoursData,
                consultationHoursData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* MODALS */}
      {/* ========================= */}

      <FacultyProfileModal
        open={modalState.open}
        mode={modalState.mode}
        showDelete={modalState.showDelete}
        title={modalState.title}
        config={modalState.config}
        initialEditValues={modalState.initialEditValues}
        onCancel={closeModal}
        onSubmit={(values) => {
          modalState.onSubmit?.(values);
          closeModal();
        }}
        onDelete={() => {
          modalState.onDelete?.();
          closeModal();
        }}
      />

      <GeneratePortfolioModal
        open={portfolioModalState.open}
        onCancel={closePortfolioModal}
        termOptions={filledTermOptions}
        onSubmit={(values) => {
          portfolioModalState.onSubmit?.(values);
        }}
      />

      <FsrImportModal
        open={importModalState.open}
        onClose={closeImportModal}
        title={importModalState.title}
        importUrl={importModalState.endpoint ? `${importModalState.endpoint}/import` : undefined}
        sampleFileUrl={importModalState.sampleFileUrl}
        sampleFileName={importModalState.sampleFileName}
        columnsGuide={importModalState.columnsGuide}
        onImported={() => {
          if (importModalState.endpoint && importModalState.setter) {
            fetchData(importModalState.endpoint, importModalState.setter);
          }
        }}
      />

      <FsrBulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImported={refetchAllSections}
      />
    </div>
  );
}
